import { useCallback, useEffect, useRef, useState } from "react";
import { Row, SettingsTypes, TableBodyProps } from "../../../../types";
import Scroller from "../Scroller";
import TableRow from "../TableRow";

const rowHeight = 50;

const TableBody = (props: TableBodyProps) => {
  const {
    leftColumns,
    mainColumns,
    rightColumns,
    rows,
    bodyRef,
    stickyCols,
    showPaginator = false,
    enableRowDrag,
    onRowDragStart,
    onRowDragOver,
    onRowDrop,
    allowRowExpansion,
    onRowExpanded,
    selectRow,
    deselectRow,
    selectedRowIds = new Set(),
    loading = false,
    leftMargin,
    rightMargin,
    expandArrowIcon,
    virtualizationSettings,
    loadingPlaceholder,
    rowKey,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  const scrollTop = useRef(0);

  const [rowsLimit, setRowsLimt] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<typeof rowKey>>(
    new Set()
  );
  const [flatRows, setFlatRows] = useState<Row[]>([]);

  const [settings, setSettings] = useState<SettingsTypes>({
    rowHeight,
    visibleRowCount: 0,
    bufferRowCount: 5,
    minRowIndex: 0,
    maxRowIndex: rows.length,
    initialRowIndex: 0,
    ...virtualizationSettings,
  });

  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      visibleRowCount: rowsLimit,
      maxRowIndex: rows.length,
    }));
  }, [rows.length, rowsLimit]);

  const getData = (offset: number, limit: number) => {
    if (loading) return loadingPlaceholder;
    if (showPaginator) return flatRows;
    const start = Math.max(settings.minRowIndex, offset);
    const end = Math.min(offset + limit, settings.maxRowIndex + 1);
    return flatRows.slice(
      start - settings.minRowIndex,
      end - settings.maxRowIndex
    );
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const rowLimit = Math.floor(
      containerRef.current.clientHeight / settings.rowHeight
    );
    setRowsLimt(rowLimit);
    return () => {
      if (stickyCols?.current?.length) {
        stickyCols.current.length = 0;
      }
    };
  }, []);

  const flattenRows = useCallback(() => {
    const result: Row[] = [];
    const traverse = (rows: Row[], level = 0, parentId = "") => {
      let childIndex = 1;

      rows.forEach((row) => {
        const rowId = row?.[rowKey] || `${parentId}.${childIndex}`;
        result.push({
          ...row,
          [rowKey]: rowId,
          _level: level,
        });
        childIndex++;
        if (expandedRows.has(rowId) && row.children) {
          traverse(row.children, level + 1, rowId);
        }
      });
    };
    traverse(rows as Row[]);
    return result;
  }, [expandedRows, rows]);

  useEffect(() => {
    setFlatRows(flattenRows());
  }, [rows, expandedRows, flattenRows]);

  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      maxRowIndex: flatRows.length,
    }));
  }, [flatRows.length]);

  const handleRowToggle = (rowData: Row) => {
    setExpandedRows((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(rowData?.[rowKey])) {
        newExpanded.delete(rowData?.[rowKey]);
      } else {
        newExpanded.add(rowData?.[rowKey]);
        if (onRowExpanded) {
          onRowExpanded(rowData);
        }
      }
      return newExpanded;
    });
  };

  return (
    <div
      className={`div-grid-body ${showPaginator ? "override-height" : ""}`}
      ref={containerRef}
    >
      <Scroller
        get={getData}
        settings={settings}
        showPaginator={showPaginator}
        className="div-grid sticky-column-container left-sticky-columns hide-scrollbar"
        rowKey={rowKey}
        renderRow={(row) => (
          <TableRow
            row={row}
            columns={leftColumns}
            stickyClass="sticky-left"
            enableRowDrag={enableRowDrag}
            onRowDragStart={onRowDragStart}
            onRowDragOver={onRowDragOver}
            onRowDrop={onRowDrop}
            allowRowExpansion={allowRowExpansion}
            handleRowToggle={handleRowToggle}
            expandedRows={expandedRows}
            rowKey={rowKey}
            selectRow={selectRow}
            deselectRow={deselectRow}
            isRowSelected={selectedRowIds.has(row.id as string)}
            loading={loading}
            expandArrowIcon={expandArrowIcon}
            settings={settings}
          />
        )}
        controlledRef={(el) => {
          if (!el) return;
          const isClassPresent = stickyCols?.current?.find((ele) => {
            const classes = Array.from(ele.classList);
            return classes.includes("left-sticky-columns");
          });
          if (!isClassPresent) stickyCols?.current?.push(el);
        }}
        onScroll={(event) => {
          const target = event.target as HTMLElement;
          scrollTop.current = target.scrollTop;
        }}
        scrollTop={scrollTop.current}
      />
      <Scroller
        get={getData}
        settings={settings}
        className="div-grid main-content hide-scrollbar"
        rowKey={rowKey}
        style={{
          maxWidth: `calc(100% - ${rightColumns?.reduce(
            (acc, col) => acc + (col.width ?? 200) - 1,
            0
          )}px)`,
          overflow: "scroll",
          marginLeft: leftMargin,
          marginRight: rightMargin,
        }}
        renderRow={(row) => (
          <TableRow
            row={row}
            columns={mainColumns}
            enableRowDrag={enableRowDrag}
            onRowDragStart={onRowDragStart}
            onRowDragOver={onRowDragOver}
            onRowDrop={onRowDrop}
            allowRowExpansion={allowRowExpansion}
            handleRowToggle={handleRowToggle}
            expandedRows={expandedRows}
            rowKey={rowKey}
            selectRow={selectRow}
            deselectRow={deselectRow}
            isRowSelected={selectedRowIds.has(row.id as string)}
            loading={loading}
            expandArrowIcon={expandArrowIcon}
            settings={settings}
          />
        )}
        controlledRef={bodyRef}
        onScroll={(event) => {
          const target = event.target as HTMLElement;
          scrollTop.current = target.scrollTop;
        }}
        scrollTop={scrollTop.current}
        showPaginator={showPaginator}
      />
      <Scroller
        get={getData}
        settings={settings}
        rowKey={rowKey}
        className="div-grid sticky-column-container right-sticky-columns"
        style={{
          gridTemplateColumns: rightColumns
            ?.map((col) => `${col.width}px`)
            .join(" "),
        }}
        showPaginator={showPaginator}
        renderRow={(row) => (
          <TableRow
            row={row}
            columns={rightColumns}
            enableRowDrag={enableRowDrag}
            onRowDragStart={onRowDragStart}
            onRowDragOver={onRowDragOver}
            onRowDrop={onRowDrop}
            allowRowExpansion={allowRowExpansion}
            stickyClass="sticky-right"
            rowKey={rowKey}
            handleRowToggle={handleRowToggle}
            expandedRows={expandedRows}
            isRowSelected={selectedRowIds.has(row.id as string)}
            loading={loading}
            expandArrowIcon={expandArrowIcon}
            settings={settings}
          />
        )}
        controlledRef={(el) => {
          if (!el) return;
          const isClassPresent = stickyCols?.current?.find((ele) => {
            const classes = Array.from(ele.classList);
            return classes.includes("right-sticky-columns");
          });
          if (!isClassPresent) stickyCols?.current?.push(el);
        }}
        onScroll={(event) => {
          const target = event.target as HTMLElement;
          scrollTop.current = target.scrollTop;
        }}
        scrollTop={scrollTop.current}
      />
    </div>
  );
};

export default TableBody;
